import { useCallback, useMemo } from "react";

import { Form, FormField, FormikErrorMessages, FormikForm, GridTwoColumns } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import type { LayoutSettings } from "../types";
import { LengthBehaviorSelect } from "./LengthBehaviorSelect";

type LayoutSettingsFormValues = Pick<LayoutSettings, "horizontalBehavior" | "verticalBehavior">;

type Props = {
  layoutSettings: LayoutSettings;
  onLayoutSettingsChange: (layoutSettings: Partial<LayoutSettings>) => void;
  enableFitContent: boolean;
  disabled?: boolean;
};

export const LayoutSettingsForm = ({ layoutSettings, onLayoutSettingsChange, enableFitContent, disabled }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);

  const initialValues: LayoutSettingsFormValues = useMemo(() => {
    const verticalBehavior =
      !enableFitContent && layoutSettings.verticalBehavior === "fit"
        ? "fill"
        : (layoutSettings.verticalBehavior ?? "fill");
    const horizontalBehavior =
      !enableFitContent && layoutSettings.horizontalBehavior === "fit"
        ? "fill"
        : (layoutSettings.horizontalBehavior ?? "fill");

    return {
      verticalBehavior,
      horizontalBehavior,
    };
  }, [layoutSettings, enableFitContent]);

  const handleSubmit = useCallback(
    (values: LayoutSettingsFormValues) => {
      onLayoutSettingsChange(values);
    },
    [onLayoutSettingsChange],
  );

  return (
    <FormikForm<LayoutSettingsFormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      submitOnBlur
      dataTest="layout-settings-form"
      validateOnChange={false}
    >
      <Form>
        <GridTwoColumns>
          <FormField<LayoutSettingsFormValues> name="horizontalBehavior">
            <LengthBehaviorSelect
              orientation="horizontal"
              tooltip={formatMessage("horizontalBehavior")}
              placeholder={formatMessage("horizontalBehavior")}
              enableFitContent={enableFitContent}
              disabled={disabled}
            />
          </FormField>

          <FormField<LayoutSettingsFormValues> name="verticalBehavior">
            <LengthBehaviorSelect
              orientation="vertical"
              tooltip={formatMessage("verticalBehavior")}
              placeholder={formatMessage("verticalBehavior")}
              enableFitContent={enableFitContent}
              disabled={disabled}
            />
          </FormField>
        </GridTwoColumns>

        <FormikErrorMessages />
      </Form>
    </FormikForm>
  );
};

const formatStrings = {
  horizontalBehavior: {
    en: "Horizontal Behavior",
  },
  verticalBehavior: {
    en: "Vertical Behavior",
  },
} satisfies FormatMessages;
